<template>
  <b-container fluid
               id="disclosure-form">
    <!-- Spinner -->
    <loading :active="loading"
             :is-full-page="true"
             :z-index="1000"/>
    <!-- /Spinner -->

    <div id="disclosure-detail-form">
      <b-card>
        <div class="card-header"
             id="title-card">
          <!-- 보고서 ID -->
          <span v-if="disclosure.disclosureId !== ''"
                class="project-id">{{ disclosure.disclosureId }}</span>
          <!-- /보고서 ID -->

          <div class="title-wrap">
            <b-form-row class="align-items-end">
              <!-- 보고서명 -->
              <b-col cols="9">
                <b-form-group>
                  <b-form-input
                      name="title"
                      label="Title"
                      id="title"
                      ref="title"
                      placeholder="보고서명을 입력해주세요"
                      size="xl"
                      class="my-1"
                      style="height: 45px; font-size: 30px; border:none;"
                      v-model="disclosure.name"
                      v-validate="{ required: true }"
                      :state="validateState('title')"
                  />
                </b-form-group>
              </b-col>
              <!-- 보고서명 -->

              <!-- 보고서 담당자 -->
              <b-col cols="3">
                <label for="assignee">담당자</label>
                <b-form-group>
                  <b-form-select id="assignee-list"
                                 name="assignee"
                                 size="sm"
                                 ref="assignee"
                                 v-validate="{ required: true, }"
                                 :state="validateState('assignee')"
                                 v-model="disclosure.manager"
                                 :options="members"
                                 class="custom-select">
                  </b-form-select>
                </b-form-group>
              </b-col>
              <!-- /보고서 담당자 -->
            </b-form-row>
          </div>
        </div>

        <!-- 보고서 상세 영역 (상태, 시작/종료 일자, 설명) -->
        <div class="project-form-wrap">
          <div class="left-form-area">
            <!-- 상태 -->
            <div class="input-wrap">
              <label for="">상태</label>
              <b-dropdown right
                          :text="getTextOfOptions(disclosure.status)"
                          size="lg"
                          class="btn-select-status"
                          :class="getStatusClass(disclosure.status)">
                <b-dropdown-item v-for="option in disclosureStatusOptions"
                                 :key="option.value"
                                 :value="option.value"
                                 @click="disclosure.status = option.value">{{ option.text }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <!-- /상태 -->

            <!-- 시작/종료 일자 -->
            <div class="input-wrap">
              <label for="start-date">시작 - 종료일</label>
              <div class="date-wrap">
                <b-form-group>
                  <b-input-group class="form-inline">
                    <b-form-input
                        id="start-date"
                        name="start-date"
                        type="date"
                        v-model="disclosure.startDate"
                        v-validate="dateRule"
                        class="custom-input rounded"
                        placeholder="YYYY-MM-DD"
                        :state="validateState('start-date')"
                    />
                  </b-input-group>
                </b-form-group>
                <b-form-group>
                  <b-input-group class="form-inline">
                    <b-form-input
                        id="end-date"
                        name="end-date"
                        type="date"
                        v-model="disclosure.endDate"
                        v-validate="dateRule"
                        class="custom-input rounded"
                        placeholder="YYYY-MM-DD"
                        :state="validateState('end-date')"
                    />
                  </b-input-group>
                </b-form-group>
              </div>
            </div>
          </div>
          <!-- 시작/종료 일자 -->

          <!-- 보고서 상세 설명 -->
          <div class="right-form-area">
            <label for="description">설명</label>
            <b-form-textarea
                class="description"
                id="description"
                name="description"
                placeholder="해당 보고서의 방향성 및 주제 설명"
                v-model="disclosure.description"
                v-validate="{required: true}"
                rows="5"
                :state="validateState('description')"
                style="height: 139px;"
            />
          </div>
          <!-- 보고서 상세 설명 -->
        </div>
        <!-- 보고서 상세 영역 (상태, 시작/종료 일자, 설명) -->
      </b-card>

      <!-- ESG 공시 보고서 목차 및 참여 인원 -->
      <b-card no-body
              class="disclosure-tab-card">
        <b-tabs>
          <b-tab title="목차">
            <esg-toc-management-grid ref="tocManagementGrid"
                                     :tableOfContents="disclosureItems"
                                     :deletedItems="deletedItems"
                                     :members="members"/>
          </b-tab>
          <b-tab title="참여 인력">
            <div class="members-wrap">
              <!-- Card-02 > Members > Users START -->
              <div class="members-content users">
                <esg-base-list name="non-members"
                               ref="non-members-list"
                               title="참여 가능 인력"
                               :selectable="true"
                               :selected-items="selectedNonMembers"
                               :variable-height="true"
                               size="sm"
                               :resource="`esg/disclosure/disclosures/${this.id}/non-members`">
                  <template #actions>
                    <esg-base-button variant="secondary"
                                     size="sm"
                                     @click="addMembers">추가
                    </esg-base-button>
                  </template>
                </esg-base-list>
              </div>
              <!-- Card-02 > Members > Users END -->

              <!-- Card-02 > Members > Member START -->
              <div class="members-content members">
                <esg-base-list name="members"
                               ref="members-list"
                               title="참여 인력"
                               :selectable="true"
                               :selected-items="selectedMembers"
                               :variable-height="true"
                               size="sm"
                               :resource="`esg/disclosure/disclosures/${this.id}/members`">
                  <template #actions>
                    <esg-base-button variant="secondary"
                                     size="sm"
                                     @click="removeMembers">제외
                    </esg-base-button>
                  </template>
                </esg-base-list>
              </div>
              <!-- Card-02 > Members > Member END -->
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- Buttons -->
    <div class="btn-container">
      <div class="btn-wrap">
        <esg-base-button @click="() => this.pushRoute('/esg/disclosures')"
                         class="mr-2"
                         variant="secondary">
          닫기
        </esg-base-button>
        <esg-base-button @click="updateDisclosure"
                         variant="primary">
          업데이트
        </esg-base-button>
      </div>
    </div>
    <!-- /Buttons -->
  </b-container>
</template>

<script>
import EsgTocManagementGrid from "./EsgDisclosureManagementGrid.vue";
import EsgBaseButton from "@/components/esg/base/EsgBaseButton.vue";
import EsgBaseList from "@/components/esg/base/EsgBaseList.vue";

export default {
  props: ["id"],
  components: {
    EsgBaseList,
    EsgBaseButton,
    EsgTocManagementGrid
  },
  async created() {
    // Custom error messages
    const customDictionary = {
      custom: {
        title: {
          required: '보고서명을 입력해주세요',
        },
        assignee: {
          required: '담당자를 지정해주세요',
        },
        description: {
          required: '보고서에 대한 설명을 입력해주세요',
        },
        'start-date': {
          required: '시작일을 입력해주세요',
          regex: '형식에 맞춰 시작일을 입력해주세요',
        },
        'end-date': {
          required: '종료일을 입력해주세요',
          regex: '형식에 맞춰 종료일을 입력해주세요',
        },
      }
    };
    this.$validator.localize('kr', customDictionary);

    await this.retrieveDisclosure();
    await this.retrieveMembers();
  },
  activated() {
    this.retrieveDisclosure();
  },
  data() {
    return {
      loading: false,
      disclosure: {
        disclosureId: "",
        name: "",
        leader: "",
        manager: "",
        status: "",
        startDate: "",
        endDate: "",
        description: "",

        creator: "",
        creationDate: "",
        updater: "",
        updatedDate: "",
      },

      disclosureItems: [],
      deletedItems: [],

      members: [],
      selectedNonMembers: [],
      selectedMembers: [],

      dateRule: {
        required: true,
        regex: /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/,
      },
    }
  },
  computed: {
    disclosureStatusOptions() {
      return [
        { value: "OPEN", text: "OPEN", disabled: false, update: false },
        { value: "CLOSED", text: "CLOSED", disabled: false, update: true },
      ]
    },
  },
  methods: {
    async retrieveDisclosure() {
      try {
        this.loading = true;
        let response = await axios.get('esg/disclosure/disclosures/' + this.id, {
          headers: { Authorization: accessToken() }
        });
        this.disclosure = response.data;
        this.disclosureItems = response.data.tableOfContents;
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async retrieveMembers() {
      try {
        this.loading = true;
        let response = await axios.get(`esg/disclosure/disclosures/${this.id}/members`, {
          headers: { Authorization: accessToken() }
        });
        let members = response.data.list;
        if (!members) return;
        members.forEach(member => {
          member.value = member.userId;
          member.text = member.userName;
        });
        members.sort((a, b) => a.text.localeCompare(b.text));
        this.members = members;
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async updateDisclosure() {
      try {
        this.loading = true;

        const result = await this.$validator.validate();
        if (!result) {
          this.showErrorsInToast();
          return;
        }

        this.preprocess(this.disclosure.tableOfContents);
        await axios.patch(
            'esg/disclosure/disclosures/' + this.disclosure.disclosureId,
            this.disclosure,
            { headers: { Authorization: accessToken() } }
        );

        if (this.deletedItems.length > 0) {
          await axios.delete('esg/disclosure/items', {
            headers: { Authorization: accessToken() },
            params: { disclosureItemIds: this.deletedItems }
          });
          this.deletedItems = [];
        }

        this.loading = false;
        this.showMessageInToast(`보고서가 업데이트되었습니다.`);
      } catch (error) {
        this.loading = false;
        await this.showError(error.message);
      } finally {
        this.loading = false;
      }
    },
    preprocess(root) {
      if (typeof root === "object" && root.children) {
        root = root.children;
      }

      root.forEach((child, index) => {
        // Remove parent field to remove circular reference (if not, causes an error when converting to JSON)
        delete child.parent;

        // Remove UUID (if not, causes an error when converting to JSON)
        if (child.created) {
          delete child.id;
        }

        // Set order among its siblings
        child.order = index;
        this.preprocess(child);
      });
    },
    async addMembers() {
      try {
        await axios.post(`esg/disclosure/disclosures/${this.id}/members`, this.selectedNonMembers, {
          headers: { Authorization: accessToken() }
        });

        // Reload non-member/member lists and assignee options
        await Promise.all([
          this.$refs["non-members-list"].initialize(),
          this.$refs["members-list"].initialize(),
          this.retrieveMembers()
        ]);

        this.selectedNonMembers = [];
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async removeMembers() {
      try {
        await axios.delete(`esg/disclosure/disclosures/${this.id}/members`, {
          headers: { Authorization: accessToken() },
          params: { disclosureMemberIds: this.selectedMembers.map(member => member.userId) }
        });

        // Reload non-member/member lists and assignee options
        await Promise.all([
          this.$refs["non-members-list"].initialize(),
          this.$refs["members-list"].initialize(),
          this.retrieveMembers()
        ]);

        this.selectedMembers = [];
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    getStatusClass(status) {
      if (status === 'OPEN') return 'open'
      else if (status === 'CLOSED') return 'closed'
    },
    validateState(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.errors.has(ref);
      }
      return null;
    },
    getTextOfOptions(status) {
      const matchedOption = this.disclosureStatusOptions.find(option => option.value === status);
      return matchedOption ? matchedOption.text : '';
    },
  },
}
</script>

<style scoped>
.btn-wrap .btn-container button {
  width: 100px;
}
</style>

<style>
/* import font */
@import url('https://hangeul.pstatic.net/hangeul_static/css/nanum-gothic.css');

#disclosure-form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

#disclosure-form .custom-select {
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  height: 2.666rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 12px) 9px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
}

span.required {
  color: red;
}

.custom-input {
  height: 2rem !important;
  line-height: 1.0 !important;
}

#disclosure-form .dropdown-toggle {
  height: 2em !important;
  padding: 0 !important;
}

#disclosure-form .dropdown-item {
  height: 1.9rem;
  font-size: 0.8em !important;
}

#disclosure-form .b-dropdown-form {
  height: 3em;
}

#disclosure-form #description {
  height: 4em;
}

#disclosure-form .btn {
  width: 100%;
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  font-family: 'NanumGothic';
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
}

#disclosure-form .btn:focus {
  box-shadow: unset;
}

#disclosure-form .btn-select-status .dropdown-toggle {
  position: relative;
  display: flex !important;
  align-items: center !important;
  gap: 0 0.933rem !important;
  min-height: 2.666rem;
  padding: 0.533rem 2.8rem 0.533rem 0.8rem !important;
  color: #1B1B1B;
  background-color: #FFF;
  font-family: 'NanumGothic' !important;
  font-size: 17px !important;
  font-weight: 400 !important;
  line-height: 42px !important;
}

#disclosure-form .btn-select-status .dropdown-toggle:hover {
  background-color: #FFF;
}

#disclosure-form .badge-container .badge.open {
  background-color: #CFDDFF;
  color: #1755CC;
}

#disclosure-form .btn-select-status .dropdown-toggle::after {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-left: 0;
  content: "";
  border: none;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
}

#disclosure-form .card .card-body {
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 1%;
}

#disclosure-form .card .card-header {
  background-color: white;
}

#disclosure-form .card #role-card-header,
#disclosure-form .card #methodology-card-header {
  margin-bottom: 1em !important;
}

#disclosure-form .card {
  margin-bottom: 2rem !important;
  border-radius: 30px !important;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.05);
}

#disclosure-form .card > .card-body {
  padding-top: max(2rem, 1.5625vw);
  padding-left: 2.0833vw;
  padding-right: 2.0833vw;
}

#disclosure-form .card .card-body .card-body {
  padding: 0;
}

#disclosure-form .card .card-body.fold {
  height: 0;
  min-height: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  border-top: none !important;
}

#disclosure-form .card.description .card-body {
  padding: 0.666rem 0.666rem 1.666rem;
}

#disclosure-form input.form-control {
  height: 40px !important;
  background-color: #FFF;
}

#disclosure-form input.form-control.is-valid {
  border-color: #E7E7EE;
}

#disclosure-form input[type="date"].form-control {
  padding: 8px 12px;
}

#disclosure-form input#projectId {
  height: 45px !important;
  padding: 0;
  margin-bottom: 0;
  background-color: #FFF;
  border: none;
  color: #1B1B1B;
  font-family: 'NanumGothic';
  font-size: 30px;
  font-weight: 400;
  line-height: 150%;
}

#disclosure-form input#projectId::placeholder {
  color: #AAA;
  font-size: 30px;
}

#disclosure-form .project-id {
  display: inline-block;
  margin-bottom: 1rem;
  padding: 8px 16px;
  border-radius: 38px;
  text-align: center;
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
  background-color: #F6F6FA;
  color: #2E2E38;
}

#disclosure-form .form-control#title {
  height: 45px !important;
  padding: 0;
  background-color: transparent;
  border: none;
  font-weight: 700;
}

#disclosure-form .form-control#title::placeholder {
  color: #AAA;
  font-size: 30px;
}

#disclosure-form .btn-select-status .dropdown-toggle-original {
  display: flex;
  align-items: center;
  gap: 0 0.933rem;
  height: 100%;
  padding: 0.266rem 1.066rem 0.266rem 1.6rem;
  border: none;
  border-radius: 38px;
  color: #FFF;
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 500;
  line-height: 42px;
}

#disclosure-form .add-wrap .card-title-area {
  display: flex;
  align-items: center;
  gap: 0 0.533rem;
}

#disclosure-form .add-wrap .card-title-area .title-text {
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
}

#disclosure-form .card-title-area {
  display: flex;
  align-items: center;
  gap: 0 0.533rem;
}

#disclosure-form .card-title-area .title-text {
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
}

#disclosure-form .add-wrap .card.user {
  width: 33%;
  overflow: hidden;
}

#disclosure-form .add-wrap .card.member {
  width: 67%;
  overflow: hidden;
}

#disclosure-form .add-wrap {
  display: flex;
  gap: 0 2rem;
}

#disclosure-form .add-wrap .card > .card-body {
  padding: 0;
}

#disclosure-form .add-wrap .card .card-body .card-header {
  padding: 2rem 2.0833vw;
  border-radius: 30px 30px 0 0;
}

#disclosure-form .add-wrap .card .card-body .card-body {
  padding: 0 2.0833vw;
  margin-bottom: 0.4rem;
  max-height: 40.333rem;
  min-height: 15.333rem;
  overflow-y: auto;
}

#disclosure-form .b-form-tags .badge {
  display: inline-flex !important;
  align-items: center !important;
  flex: 0 0 auto !important;
  line-height: 1 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  height: 2em;
}

#disclosure-form .description {
  border-radius: 13px !important;
  height: 4em;
  border: 1px solid #E7E7EE;
  font-family: 'NanumGothic';
  padding: 0.533rem 1.066rem;
}

#disclosure-form .card .card-body.fold {
  height: 0;
  min-height: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  border-top: none !important;
}

#disclosure-form .add-wrap .card-title-area .total-num,
#disclosure-form .card-title-area .total-num {
  min-width: 36px;
  padding: 2px 8px;
  border-radius: 40px;
  background-color: #FFE500;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  text-align: center;
}

#disclosure-form .tag-list {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 0.666rem;
  margin-left: 1.333rem;
}

#disclosure-form .tag-list::before {
  content: '';
  display: block;
  position: absolute;
  top: 50%;
  left: -20px;
  transform: translateY(-50%);
  width: 1px;
  height: 24px;
  background-color: #DDDDE3;
}

#disclosure-form .b-form-tags .badge {
  gap: 0 0.666rem;
  height: 2.666rem;
  padding: 0.266rem 0.8rem 0.266rem 1.333rem;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
  border-radius: 60px;
  border: 1px solid #DDDDE3;
  background-color: #FFF;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.05);
}

#disclosure-form label {
  font-family: 'NanumGothic';
}

#disclosure-form .title-wrap .input-wrap:first-child {
  min-width: 393px;
  flex-shrink: 0;
}

#disclosure-form .project-form-wrap {
  display: flex;
  gap: 2rem;
  margin-top: 2.666rem;
}

#disclosure-form .project-form-wrap .input-wrap {
  display: flex;
  flex-direction: column;
}

#disclosure-form .project-form-wrap .left-form-area {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 393px;
  flex-shrink: 0;
}

#disclosure-form .project-form-wrap .left-form-area .date-wrap {
  display: flex;
  gap: 0 1.333rem;
}

#disclosure-form .project-form-wrap .date-wrap .form-group {
  width: calc(50% - 10px);
}

#disclosure-form .project-form-wrap .date-wrap .form-group:first-child {
  position: relative;
}

#disclosure-form .project-form-wrap .date-wrap .form-group:first-child::after {
  content: '~';
  display: block;
  position: absolute;
  top: 50%;
  right: -15px;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  color: #747480;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
}

#disclosure-form .project-form-wrap .date-wrap .custom-input {
  height: 2.666rem !important;
  background-color: #FFF;
}

#disclosure-form .project-form-wrap .right-form-area {
  flex-grow: 1;
  flex-shrink: 0;
}

#disclosure-form .input-wrap.border-bottom {
  padding-bottom: 20px;
  border-bottom: 1px solid #DDDDE3;
}

#disclosure-form .input-wrap.title {
  flex-grow: 1;
  flex-shrink: 0;
}

/* 목차 및 멤버 관리 탭 */
#disclosure-form .disclosure-tab-card .card-header {
  border-radius: 20px 20px 0 0;
}

#disclosure-form .disclosure-tab-card .nav-tabs {
  gap: 0 30px;
  padding: 20px 0 0;
  margin: 0 30px;
  border-bottom: 1px solid #DDDDE3;
}

#disclosure-form .disclosure-tab-card .nav-tabs .nav-item {
  margin-bottom: 0;
}

#disclosure-form .disclosure-tab-card .nav-tabs .nav-link {
  color: #747480;
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: none;
}

#disclosure-form .disclosure-tab-card .nav-tabs .nav-link.active,
#disclosure-form .disclosure-tab-card .nav-tabs .nav-item.show .nav-link {
  color: #2E2E38;
  font-weight: 700;
  border-bottom: 3px solid #2E2E38;
}

#disclosure-form .disclosure-tab-card .nav-tabs .nav-link.disabled {
  color: #C4C4CD;
}

#disclosure-form .disclosure-tab-card .tab-content {
  padding: 20px;
}

#disclosure-form .card .card-body.tab-pane {
  padding: 0;
}

#disclosure-form .disclosure-tab-card .custom-select {
  padding: 0.533rem 2.266rem 0.533rem 1.066rem;
  height: 2.666rem !important;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #FFF;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 12px) 9px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
}

#disclosure-form .disclosure-tab-card .custom-select:focus {
  border-color: #E7E7EE;
  box-shadow: unset;
}

#disclosure-form .disclosure-tab-card .members-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

#disclosure-form .disclosure-tab-card .members-wrap .card {
  box-shadow: unset;
  border: 1px solid #E7E7EE;
}

#disclosure-form .disclosure-tab-card .members-wrap .card-header,
#disclosure-form .disclosure-tab-card .members-wrap .card-footer {
  background-color: transparent !important;
}

#disclosure-form .disclosure-tab-card .members-wrap .card-body {
  padding: 1.125rem !important;
}
</style>
